import React, { useContext, useState, useEffect, useRef } from 'react'
import { View, StyleSheet, TouchableOpacity } from 'react-native'
import { Text, Layout, Icon, useTheme } from '@ui-kitten/components'
import { UserContext } from '../../global/UserProvider'
import Divider from '../../components/Divider'
import { useTranslation } from 'react-i18next'
import { dateRangeWithTimezoneAndAutoclose, getStoreBusinessDate } from '../../utils/timezoneFunctions'
import { useAtomValue } from 'jotai'
import { constantsAtom, currencyFormatterAtom, dailyStatsAtom, productsDictAtom, storesDictAtom, userRoleAtom } from '../../global/AtomsProvider'
import { recurringSpendingAmountForRange } from '../Spendings/recurringSpendingAmount'
import { countEmployeeBonuses } from '../../global/Firebase/workingHours'
import { rethrowUnlessWRefusal } from '../../global/Write/writeW'
import { format } from 'date-fns'

import { getMonthlyWorkingHoursOnDateRange } from '../../global/Firebase/workingHours'
import { validateDailyStats } from '../../utils/validationFunctions'
import { triggerDailyStatsValidation } from '../../utils/dailyStatsValidationSimple'

export default function MainStats( { style, store, navigation, route } ) {
  const theme = useTheme()
  const { t } = useTranslation()
  const [context, setContext] = useContext(UserContext)
  const productsDict = useAtomValue(productsDictAtom)
  const dailyStats = useAtomValue(dailyStatsAtom)
  const currencyFormatter = useAtomValue(currencyFormatterAtom)
  const userRole = useAtomValue(userRoleAtom)
  const constants = useAtomValue(constantsAtom)
  const [stats, setStats] = useState({
    saleDifference: (dailyStats[store]?.saleAmount || 0) - (dailyStats[store]?.salesPurchaseAmount || 0),
    inStockPurchaseAmount: 0,
    inStockSaleAmount: 0,
    monthlySalary: 0
  })

  // Ref to track if validations have been triggered to prevent duplicate calls
  const validationsTriggeredRef = useRef(false)

  useEffect(() => {
    countInStockAmount()
    countMonthlySalary()
    countMonthlySpendings()
  }, [store])

  // trigger validations
  useEffect(() => {
    // Prevent duplicate calls (e.g., from React Strict Mode)
    if (validationsTriggeredRef.current) {
      return
    }
    validationsTriggeredRef.current = true

    let yesterday = new Date()
    yesterday.setDate(yesterday.getDate() - 1)
    // Get screen name for logging
    const screenName = route?.name || navigation?.getState?.()?.routes?.[navigation?.getState?.()?.index]?.name || 'MainStats'
    // The trailing `store` is the set the range was ENCODED with (ATL-1112) — it only decodes
    // the bounds, and deliberately does not change which working hours are processed.
    // Not awaited, as before. A W refusal (staging cut-over preview) was already announced by the guard;
    // anything else rethrows as before (P9b review 1).
    countEmployeeBonuses(null, dateRangeWithTimezoneAndAutoclose({startDate: new Date(), endDate: new Date()}, [store]), null, null, null, false, screenName, store).catch(rethrowUnlessWRefusal)
    countEmployeeBonuses(null, dateRangeWithTimezoneAndAutoclose({startDate: yesterday, endDate: yesterday}, [store]), null, null, null, false, screenName, store).catch(rethrowUnlessWRefusal)
    // Trigger simple daily stats validation (checks if 3+ hours passed since last check)
    triggerDailyStatsValidation()
  }, [])

  const countMonthlySpendings = () => {
    // ATL-1112: today's share of each monthly expense, prorated in the STORE's frame.
    // This used to read `new Date()` through device-local getters, so on a device outside the
    // store's timezone it landed on the wrong day and — just after local midnight around a
    // month boundary — divided by the wrong month's length (105000/31 on 1 September).
    var rSAmount = 0
    // Today's TRADING day in the store's own frame — not the device's calendar day, and not a
    // window edge, so it is autoclose-aware on both edges.
    const today = format(getStoreBusinessDate(store, new Date()), 'yyyy-MM-dd')
    const window = { startDate: today, endDate: today }

    for (const rS of constants.recurringSpendings.data) {
      if (rS.store === store || rS.store?.id === store) {
        rSAmount += recurringSpendingAmountForRange(rS, window, store)
      }
    }

    setStats(stats => {return {...stats, rSAmount}})
  }

  const countMonthlySalary = async () => {
    // get monthly salaries and divide them by number of days
    let selectedDate = new Date()
    let monthlyWorkingHours = await getMonthlyWorkingHoursOnDateRange({startDate: selectedDate, endDate: selectedDate}, store === 'all' ? null : store)
    let salaryAmount = 0
    for (let monthlyWorkingHour of monthlyWorkingHours) {
      let startDate = monthlyWorkingHour.startDate.toDate()
      let endDate = monthlyWorkingHour.endDate.toDate()
      
      // Get the month of the selected date
      const month = selectedDate.getMonth()
      const year = selectedDate.getFullYear()
      
      // Get number of days in the selected date's month
      const daysInMonth = new Date(year, month + 1, 0).getDate()
      
      // Check if the selected date falls within the salary period
      if (selectedDate >= startDate && selectedDate <= endDate) {
        // If this is a full month salary and the selected date is in that month
        if (startDate.getDate() === 1 && 
            endDate.getDate() === new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0).getDate() &&
            startDate.getMonth() === month && 
            startDate.getFullYear() === year) {
          // For a full month salary record, divide by days in month
          salaryAmount += monthlyWorkingHour.amount / daysInMonth
        } else {
          // For partial month records, calculate the daily rate
          const totalDays = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1
          salaryAmount += monthlyWorkingHour.amount / totalDays
        }
      }
    }
    setStats(stats => {return {...stats, monthlySalary: salaryAmount}})
  }

  const countInStockAmount = () => {
    var inStockPurchaseAmount = 0
    var inStockSaleAmount = 0
    var inStockQuantity = 0
    Object.values(productsDict).forEach(product => {
      if (product.activeInStore?.[store] && !product.isComposite) {
        inStockPurchaseAmount += (product.storeStock?.[store] || 0) * (product.purchasePrice || 0)
        inStockSaleAmount += (product.storeStock?.[store] || 0) * (product.salePrices[store] || 0)
        inStockQuantity += product.storeStock?.[store] || 0
      }
    })
    setStats(stats => {return {...stats, inStockQuantity, inStockPurchaseAmount, inStockSaleAmount, saleDifference: (dailyStats[store]?.saleAmount || 0) - (dailyStats[store]?.salesPurchaseAmount || 0)}})
  }

  return (
    <View style={style}>
      <Text category='label' style={styles.header}>
        {t('stats.today')}
      </Text>
      <Layout style={styles.tableView}>
        <View style={styles.rowItem}>
          <Text category='s2' style={styles.rowItemName}>{t('stats.name')}</Text>
          <Text category='s2' style={styles.rowItemQuantity}>{t('stats.quantityShort')}</Text>
          <Text category='s2' style={styles.rowItemAmount}>{t('stats.amount')}</Text>
        </View>
        <Divider width='100%' marginTop={7} />
        <View style={styles.rowItem}>
          <Text category='label' style={styles.rowItemName}>{t('stats.sales')}</Text>
          <Text style={styles.rowItemQuantity}>{(dailyStats[store]?.saleQuantity || 0)}</Text>
          <Text style={styles.rowItemAmount}>{currencyFormatter.format(dailyStats[store]?.saleAmount || 0)}</Text>
        </View>
        { constants?.purchaseMethods && constants.purchaseMethods.data.map((item, index) => (
          <View style={styles.rowItem} key={item+index}>
            <Text category='label' style={styles.rowItemName}>{' - ' + item.name}</Text>
            <Text style={styles.rowItemQuantity}>{(dailyStats[store]?.[item.id+'SalesQuantity'] || 0)}</Text>
            <Text style={styles.rowItemAmount}>{currencyFormatter.format(dailyStats[store]?.[item.id+'SalesAmount'] || 0)}</Text>
          </View>
        ))}
        <View style={styles.rowItem}>
          <Text category='label' style={styles.rowItemName}>{t('stats.salesMargin')}</Text>
          <Text style={styles.rowItemAmount}>{currencyFormatter.format(stats.saleDifference)}</Text>
        </View>
        <View style={styles.rowItem}>
          <Text category='label' style={styles.rowItemName}>{t('stats.loyalClients')}</Text>
          <Text style={styles.rowItemQuantity}>{(dailyStats[store]?.bonusClients || 0) + ' (+' + (dailyStats[store]?.newBonusClients || 0) + ')'}</Text>
          <Text style={styles.rowItemAmount}>{'+' + currencyFormatter.format(dailyStats[store]?.bonusAccumulation || 0) + ' / -' + currencyFormatter.format(dailyStats[store]?.bonusReduction + dailyStats[store]?.punchCardReduction || 0)}</Text>
        </View>
        <View style={styles.rowItem}>
          <Text category='label' style={styles.rowItemName}>{t('stats.returns')}</Text>
          <Text style={styles.rowItemQuantity}>{(dailyStats[store]?.returnsQuantity || 0)}</Text>
          <Text style={styles.rowItemAmount}>{currencyFormatter.format(dailyStats[store]?.returnsAmount || 0)}</Text>
        </View>
        <View style={styles.rowItem}>
          <Text category='label' style={styles.rowItemName}>{t('stats.supplies')}</Text>
          <Text style={styles.rowItemQuantity}>{(dailyStats[store]?.suppliesQuantity || 0)}</Text>
          <Text style={styles.rowItemAmount}>{currencyFormatter.format(dailyStats[store]?.suppliesAmount || 0)}</Text>
        </View>
        <View style={styles.rowItem}>
          <Text category='label' style={styles.rowItemName}>{t('stats.writeOffs')}</Text>
          <Text style={styles.rowItemQuantity}>{(dailyStats[store]?.writeOffsQuantity || 0)}</Text>
          <Text style={styles.rowItemAmount}>{currencyFormatter.format(dailyStats[store]?.writeOffsAmount || 0)}</Text>
        </View>
        <View style={styles.rowItem}>
          <Text category='label' style={styles.rowItemName}>{t('stats.salaryAmount')}</Text>
          <Text style={styles.rowItemAmount}>{currencyFormatter.format((dailyStats[store]?.salaryAmount || 0) + stats.monthlySalary) + (dailyStats[store]?.employeeBonusAmount > 0 ? (' (+' + currencyFormatter.format(dailyStats[store]?.employeeBonusAmount) + ')') : '')}</Text>
        </View>
        <View style={styles.rowItem}>
          <Text category='label' style={styles.rowItemName}>{t('stats.spendings')}</Text>
          <Text style={styles.rowItemQuantity}>{(dailyStats[store]?.spendingsQuantity || 0)}</Text>
          <Text style={styles.rowItemAmount}>{currencyFormatter.format(dailyStats[store]?.spendingsAmount || 0)}</Text>
        </View>
        <View style={styles.rowItem}>
          <Text category='label' style={styles.rowItemName}>{t('stats.inStockProducts')}</Text>
          <Text style={styles.rowItemQuantity}>{(stats.inStockQuantity?.toFixed(0) || 0)}</Text>
          <Text style={styles.rowItemAmount}>{currencyFormatter.format(stats.inStockPurchaseAmount) + ' / ' + currencyFormatter.format(stats.inStockSaleAmount)}</Text>
        </View>
        <View style={styles.rowItem}>
          <View style={[{flexDirection: 'row', alignItems: 'center'}, styles.rowItemName]}>
            <Text category='label'>
              {t('stats.monthlySpendings')}
            </Text>
            { (userRole?.permissions?.['editRecurringSpendings'] || context.user.role === 'admin') && (
              <TouchableOpacity onPress={() => navigation.navigate('UniversalList', {atom: constantsAtom, initialDataPath: ['recurringSpendings', 'data'], title: t('stats.monthlySpendings'), searchField: 'name', detailScreen: 'RecurringSpendingDetail'})} style={{marginLeft: 5, padding: 3}}>
                <Icon name='settings-outline' height={15} width={15} fill={theme['color-primary-500']} />
              </TouchableOpacity>
            )}
          </View>
          <Text style={styles.rowItemAmount}>{currencyFormatter.format(stats.rSAmount)}</Text>
        </View> 
        <View style={styles.rowItem}>
          <Text category='label' style={styles.rowItemName}>{t('stats.profitForDay')}</Text>
          <Text category='s1' style={styles.rowItemAmount}>{currencyFormatter.format(stats.saleDifference 
            - ((dailyStats[store]?.salaryAmount || 0) + stats.monthlySalary) - (dailyStats[store]?.employeeBonusAmount || 0) - (dailyStats[store]?.writeOffsAmount || 0)?.toFixed(0)
            - (dailyStats[store]?.spendingsAmount || 0) - (dailyStats[store]?.returnsAmount || 0) - (stats.rSAmount || 0))}
          </Text>
        </View>
      </Layout>
    </View>
  )
}

const styles = StyleSheet.create({
  header: {
    marginTop: 10,
    marginBottom: 5
  },
  tableView: {
    padding: 10,
    marginBottom: 10,
    borderRadius: 10,
  },
  rowItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginVertical: 2,
  }, 
  rowItemName: {
    flex: 9
  }, 
  rowItemQuantity: {
    flex: 3,
    textAlign: 'center',
  }, 
  rowItemAmount: {
    flex: 4,
    textAlign: 'right',
  },
})
